import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DollarSign, Headphones, MessageSquare, Heart, Zap, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function ForCurators() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Earn Money Reviewing Music | Soundope";
    
    const metaTags = [
      { type: 'name', key: 'description', content: "Get paid to discover new music! Earn real money reviewing tracks, leave passionate feedback, and enjoy music you love. Cash out anytime via PayPal." },
      { type: 'name', key: 'keywords', content: "get paid to review music, earn money listening to music, music review jobs, paid music feedback, music reviewer income, discover new music, music discovery platform" },
      
      { type: 'property', key: 'og:title', content: 'Earn Money Reviewing Music | Soundope' },
      { type: 'property', key: 'og:description', content: 'Get paid to discover new music! Earn real money reviewing tracks and cash out via PayPal.' },
      { type: 'property', key: 'og:url', content: window.location.href },
      { type: 'property', key: 'og:type', content: 'website' },
      
      { type: 'name', key: 'twitter:card', content: 'summary_large_image' },
      { type: 'name', key: 'twitter:title', content: 'Earn Money Reviewing Music | Soundope' },
      { type: 'name', key: 'twitter:description', content: 'Get paid to discover new music! Earn real money reviewing tracks.' },
      
      { type: 'name', key: 'robots', content: 'index, follow' },
    ];

    metaTags.forEach(({ type, key, content }) => {
      let tag = document.querySelector(`meta[${type}="${key}"]`);
      if (tag) {
        tag.setAttribute('content', content);
      } else {
        tag = document.createElement('meta');
        tag.setAttribute(type, key);
        tag.setAttribute('content', content);
        document.head.appendChild(tag);
      }
    });
  }, []);

  const handleGetStarted = () => navigate("/auth?mode=signup");

  return (
    <div className="min-h-screen px-4 py-8 md:py-16 pb-32 md:pb-16 bg-[#0a0a0a]">
      <div className="max-w-4xl mx-auto">
        
        {/* Hero Section */}
        <div className="neuro-base rounded-3xl p-8 md:p-12 mb-8 border-2 border-[#c0a060]/40 bg-gradient-to-br from-[#0a0a0a] to-[#151510]">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <DollarSign className="w-10 h-10 text-[#c0a060]" />
              <h1 className="text-3xl md:text-5xl text-[#c0a060] font-creepy-zombie uppercase tracking-wide">
                GET PAID TO DISCOVER MUSIC
              </h1>
            </div>
            
            <p className="text-xl md:text-2xl text-[#d0d0d0] mb-6 font-masking-renta">
              Turn Your Music Passion Into Real Money
            </p>
            
            <p className="text-base md:text-lg text-[#a0a0a0] max-w-3xl mx-auto mb-8 leading-relaxed">
              Love discovering new tracks? We pay you to do exactly that. Listen to fresh music from independent artists, 
              leave passionate reviews, and <span className="text-[#c0a060] font-medium">cash out anytime via PayPal</span>.
            </p>
            
            <Button
              onClick={handleGetStarted}
              className="neuro-base active:neuro-pressed rounded-3xl px-10 py-6 text-xl smooth-transition hover:scale-105 border-2 border-[#c0a060]/50"
            >
              <DollarSign className="w-6 h-6 mr-2 text-[#c0a060]" />
              <span className="text-[#c0a060]">Start Earning Now</span>
            </Button>
          </div>
        </div>

        {/* How It Works */}
        <div className="neuro-base rounded-3xl p-8 md:p-12 mb-8 border border-[#252525]">
          <h2 className="text-2xl md:text-3xl font-masking-renta text-[#d0d0d0] mb-8 text-center">
            How You Earn Money
          </h2>
          
          <div className="space-y-6">
            <div className="neuro-pressed rounded-2xl p-6 border border-[#90b090]/30">
              <div className="flex items-start gap-4">
                <div className="neuro-base rounded-full w-12 h-12 flex items-center justify-center flex-shrink-0">
                  <Headphones className="w-6 h-6 text-[#90b090]" />
                </div>
                <div>
                  <h3 className="text-xl text-[#90b090] mb-2 font-medium">1. Discover New Tracks</h3>
                  <p className="text-[#a0a0a0] leading-relaxed">
                    Browse our discovery page filled with fresh music from independent artists across all genres. 
                    Find tracks that match your taste and vibe.
                  </p>
                </div>
              </div>
            </div>

            <div className="neuro-pressed rounded-2xl p-6 border border-[#9090b0]/30">
              <div className="flex items-start gap-4">
                <div className="neuro-base rounded-full w-12 h-12 flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-6 h-6 text-[#9090b0]" />
                </div>
                <div>
                  <h3 className="text-xl text-[#9090b0] mb-2 font-medium">2. Leave Passionate Reviews</h3>
                  <p className="text-[#a0a0a0] leading-relaxed">
                    Share your honest thoughts on each track. The more detailed and helpful your feedback, 
                    the more you earn. Artists value real opinions that help them grow.
                  </p>
                </div>
              </div>
            </div>

            <div className="neuro-pressed rounded-2xl p-6 border border-[#c0a060]/30">
              <div className="flex items-start gap-4">
                <div className="neuro-base rounded-full w-12 h-12 flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-6 h-6 text-[#c0a060]" />
                </div>
                <div>
                  <h3 className="text-xl text-[#c0a060] mb-2 font-medium">3. Cash Out Via PayPal</h3>
                  <p className="text-[#a0a0a0] leading-relaxed">
                    Your earnings add up with every review. When you're ready, cash out directly to your PayPal account. 
                    No minimum thresholds, no hoops to jump through.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Why Reviewers Love Soundope */}
        <div className="neuro-base rounded-3xl p-8 md:p-12 mb-8 border border-[#252525]">
          <h2 className="text-2xl md:text-3xl font-masking-renta text-[#d0d0d0] mb-8 text-center">
            Why Reviewers Love Soundope
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="neuro-pressed rounded-2xl p-5 border border-[#505050]">
              <div className="flex items-center gap-3 mb-3">
                <CheckCircle className="w-5 h-5 text-[#90b090]" />
                <h3 className="text-lg text-[#d0d0d0] font-medium">Real Money, Real Fast</h3>
              </div>
              <p className="text-[#909090] text-sm leading-relaxed">
                No waiting months for payouts. Earn credits with every review and cash out whenever you want.
              </p>
            </div>

            <div className="neuro-pressed rounded-2xl p-5 border border-[#505050]">
              <div className="flex items-center gap-3 mb-3">
                <Heart className="w-5 h-5 text-[#b06080]" />
                <h3 className="text-lg text-[#d0d0d0] font-medium">Discover Amazing Artists</h3>
              </div>
              <p className="text-[#909090] text-sm leading-relaxed">
                Find your next favorite song before it blows up. Get early access to underground talent.
              </p>
            </div>

            <div className="neuro-pressed rounded-2xl p-5 border border-[#505050]">
              <div className="flex items-center gap-3 mb-3">
                <Zap className="w-5 h-5 text-[#c0a060]" />
                <h3 className="text-lg text-[#d0d0d0] font-medium">Your Opinion Matters</h3>
              </div>
              <p className="text-[#909090] text-sm leading-relaxed">
                Help artists grow with constructive feedback. Your reviews directly impact their journey.
              </p>
            </div>

            <div className="neuro-pressed rounded-2xl p-5 border border-[#505050]">
              <div className="flex items-center gap-3 mb-3">
                <Headphones className="w-5 h-5 text-[#9090b0]" />
                <h3 className="text-lg text-[#d0d0d0] font-medium">Listen On Your Schedule</h3>
              </div>
              <p className="text-[#909090] text-sm leading-relaxed">
                Review as many or as few tracks as you want. No quotas, no pressure – just music.
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="neuro-base rounded-3xl p-8 md:p-12 border-2 border-[#90b090]/30 bg-gradient-to-br from-[#0a0a0a] to-[#0a100a] text-center">
          <h2 className="text-2xl md:text-3xl font-masking-renta text-[#90b090] mb-4">
            Ready to Get Paid for Your Music Taste?
          </h2>
          <p className="text-[#a0a0a0] mb-8 max-w-2xl mx-auto">
            Join thousands of music lovers who earn money discovering new tracks. 
            Sign up free and start reviewing today.
          </p>
          
          <Button
            onClick={handleGetStarted}
            className="neuro-base active:neuro-pressed rounded-3xl px-10 py-6 text-xl smooth-transition hover:scale-105 border-2 border-[#90b090]/50"
          >
            <span className="text-[#90b090]">Create Free Account</span>
          </Button>
          
          <p className="text-sm text-[#606060] mt-4">No credit card required</p>
        </div>

        {/* Footer Tagline */}
        <div className="text-center mt-12">
          <p className="text-xl text-[#808080] font-masking-renta">
            Dope sounds? Soundope.
          </p>
        </div>
      </div>
    </div>
  );
}
